/**
 * Access Control Center — Owner/Admin RBAC management hub.
 *
 * Tabs:
 *   • Users       — searchable list of all accounts
 *   • Pending     — approval queue
 *   • Audit Log   — owner-only immutable trail
 *   • Roles       — read-only matrix
 *
 * Owner-only actions (block, delete, impersonate, audit) are gated client-side
 * AND server-side (defense-in-depth).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Users as UsersIcon, UserPlus, Hourglass, FileLock2, Layers,
  Loader2, Search, MoreVertical, Star, ShieldOff, ShieldCheck, Trash2,
  Ban, RefreshCw, Eye, Edit3, AlertTriangle, CheckCircle2, UserCog,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_TONE = {
  owner:    'bg-rose-100 text-rose-800 border-rose-200',
  admin_l2: 'bg-amber-100 text-amber-800 border-amber-200',
  manager:  'bg-indigo-100 text-indigo-800 border-indigo-200',
  staff:    'bg-sky-100 text-sky-800 border-sky-200',
  partner:  'bg-emerald-100 text-emerald-800 border-emerald-200',
  supplier: 'bg-orange-100 text-orange-800 border-orange-200',
  buyer:    'bg-violet-100 text-violet-800 border-violet-200',
  lead:     'bg-slate-100 text-slate-700 border-slate-200',
};
const STATUS_TONE = {
  active:           'bg-emerald-100 text-emerald-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  blocked:          'bg-rose-100 text-rose-700',
  soft_deleted:     'bg-slate-100 text-slate-500',
};

const AccessControlCenter = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [audit, setAudit] = useState([]);
  const [roles, setRoles] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState(null);     // user being edited
  const [revealed, setRevealed] = useState(null);   // {email, password}

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  }), []);

  const isOwner = me?.role === 'owner';

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, uRes, pRes, rRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, auth),
        axios.get(`${API_URL}/api/access-control/users`, auth),
        axios.get(`${API_URL}/api/access-control/pending-approvals`, auth),
        axios.get(`${API_URL}/api/access-control/roles`, auth),
      ]);
      setMe(meRes.data);
      setUsers(uRes.data || []);
      setPending(pRes.data || []);
      setRoles(rRes.data || []);
      if (meRes.data?.role === 'owner') {
        const aRes = await axios.get(`${API_URL}/api/access-control/audit-log?limit=100`, auth);
        setAudit(aRes.data || []);
      }
    } catch (e) {
      toast({ title: 'Failed to load Access Control', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (u.email || '').toLowerCase().includes(q)
        || (u.full_name || '').toLowerCase().includes(q)
        || (u.role || '').toLowerCase().includes(q);
  });

  const doAction = async (path, payload = {}, method = 'post') => {
    try {
      await axios[method](`${API_URL}${path}`, payload, auth);
      await load();
      toast({ title: 'Updated' });
    } catch (e) {
      toast({ title: 'Action failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const impersonate = async (target) => {
    if (!window.confirm(
      `Start an Owner Impersonation session as ${target.email}? ` +
      `Your original session will be saved and restored when you stop.`
    )) return;
    try {
      const r = await axios.post(`${API_URL}/api/access-control/impersonate`,
        { target_email: target.email, minutes: 30 }, auth);
      // Backup current owner token + user; replace with target's token
      const ownerToken = localStorage.getItem('admin_token');
      const ownerUser  = localStorage.getItem('admin_user');
      if (ownerToken) localStorage.setItem('admin_token_owner_backup', ownerToken);
      if (ownerUser)  localStorage.setItem('admin_user_owner_backup', ownerUser);
      localStorage.setItem('admin_token', r.data.access_token);
      // Fetch /me with the new token to populate admin_user correctly
      const meR = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${r.data.access_token}` },
      });
      localStorage.setItem('admin_user', JSON.stringify(meR.data));
      toast({ title: `Impersonating ${target.email}`, description: 'Banner stays at top until you stop.' });
      setTimeout(() => { navigate('/admin/dashboard'); window.location.reload(); }, 200);
    } catch (e) {
      toast({ title: 'Impersonate failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="access-control-center">
      <BackButton />
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-serif text-[#0F172A] flex items-center gap-2">
            <Shield className="w-7 h-7 text-[#FF6B35]" />
            Access Control Center
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Approve, promote, demote, block and audit every account on the platform.
            All actions are recorded in an immutable chained audit log.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="ac-reload-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reload
          </Button>
          <Button onClick={() => setInviteOpen(true)}
            className="bg-[#0F172A] hover:bg-[#1E293B]"
            data-testid="ac-invite-btn">
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Invite
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {[
          { key: 'users',   label: `Users (${users.length})`, icon: UsersIcon },
          { key: 'pending', label: `Pending (${pending.length})`, icon: Hourglass },
          isOwner && { key: 'audit', label: 'Audit Log Master', icon: FileLock2 },
          { key: 'roles',   label: 'Roles', icon: Layers },
        ].filter(Boolean).map(t => (
          <button key={t.key}
            data-testid={`ac-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold transition-all ${
              tab === t.key
                ? 'border-b-2 border-[#FF6B35] text-[#FF6B35]'
                : 'text-slate-500 hover:text-[#0F172A]'
            }`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Card><CardContent className="py-10 text-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline" /> Loading…
        </CardContent></Card>
      ) : (
        <>
          {tab === 'users' && (
            <>
              <div className="mb-3 relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input className="pl-9" placeholder="Search by email, name or role…"
                  data-testid="ac-search-input"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="space-y-2" data-testid="ac-users-list">
                {filtered.map((u) => (
                  <UserRow key={u.id} user={u} me={me} onEdit={() => setEditing(u)}
                    onAction={doAction} onImpersonate={impersonate} />
                ))}
                {filtered.length === 0 && (
                  <Card><CardContent className="py-10 text-center text-slate-500">
                    No users match your search.
                  </CardContent></Card>
                )}
              </div>
            </>
          )}

          {tab === 'pending' && (
            <div className="space-y-2" data-testid="ac-pending-list">
              {pending.length === 0 ? (
                <Card><CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
                  <div className="text-slate-700 font-medium">No pending approvals</div>
                  <div className="text-xs text-slate-500 mt-1">All set.</div>
                </CardContent></Card>
              ) : pending.map(p => (
                <Card key={p.id} data-testid={`ac-pending-${p.id}`}>
                  <CardContent className="py-3 flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold">{p.full_name}</div>
                      <div className="text-xs text-slate-500">{p.email} · requested role: {p.role || '—'}</div>
                    </div>
                    <Button size="sm"
                      onClick={() => doAction(`/api/access-control/approve/${p.id}`, { role: p.role || 'staff', modules_access: p.modules_access || [] })}
                      data-testid={`ac-approve-${p.id}`}
                      className="bg-emerald-600 hover:bg-emerald-700">
                      Approve
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => doAction(`/api/access-control/reject/${p.id}`, { reason: 'Rejected via Access Control Center' })}
                      data-testid={`ac-reject-${p.id}`}
                      className="text-rose-600 border-rose-200">
                      Reject
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {tab === 'audit' && isOwner && (
            <Card><CardContent className="p-0">
              <ul className="divide-y divide-slate-100 text-xs" data-testid="ac-audit-list">
                {audit.length === 0 && <li className="px-4 py-6 text-center text-slate-500">No actions recorded yet.</li>}
                {audit.map((a, i) => (
                  <li key={i} className="px-3 py-2 flex items-center gap-3 hover:bg-slate-50">
                    <span className="font-mono text-slate-400 w-44 flex-shrink-0">{(a.ts || '').slice(0,19).replace('T',' ')}</span>
                    <Badge className="text-[9px] bg-slate-200 text-slate-700">{a.actor_role || '—'}</Badge>
                    <span className="font-semibold w-56 truncate">{a.actor_email}</span>
                    <Badge className={`text-[9px] ${a.severity === 'critical' ? 'bg-rose-100 text-rose-700' : a.severity === 'warning' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`}>
                      {a.action}
                    </Badge>
                    <span className="text-slate-500 truncate">{a.entity_type}{a.entity_id ? ' · ' + a.entity_id.slice(0,8) : ''}</span>
                    {a.notes && <span className="text-slate-400 truncate">— {a.notes}</span>}
                  </li>
                ))}
              </ul>
            </CardContent></Card>
          )}

          {tab === 'roles' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ac-roles-grid">
              {roles.map(r => (
                <Card key={r.key}>
                  <CardContent className="py-3">
                    <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${ROLE_TONE[r.key] || 'bg-slate-100'}`}>
                      L{r.level} · {r.label}
                    </div>
                    <div className="mt-2 text-[11px] text-slate-500">
                      Default modules: {(r.default_modules || []).join(', ') || '— (assigned per user)'}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Invite dialog */}
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)}
        roles={roles} me={me} onCreated={(p) => { setRevealed(p); load(); }} />

      {/* Edit / promote dialog */}
      {editing && (
        <EditUserDialog user={editing} me={me} roles={roles}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {/* Password reveal dialog */}
      {revealed && (
        <Dialog open={!!revealed} onOpenChange={() => setRevealed(null)}>
          <DialogContent data-testid="ac-revealed-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-600" /> Account created
              </DialogTitle>
              <DialogDescription className="sr-only">Temporary password — copy and store now.</DialogDescription>
            </DialogHeader>
            <div className="px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
              <span>This password is shown only once. Copy and securely deliver to <b>{revealed.user.email}</b>. They will be required to change it on first login.</span>
            </div>
            <code className="block p-2 mt-2 rounded bg-slate-900 text-emerald-300 text-sm font-mono break-all"
              data-testid="ac-revealed-password">{revealed.temporary_password}</code>
            <DialogFooter>
              <Button onClick={() => { navigator.clipboard.writeText(revealed.temporary_password); toast({ title: 'Copied' }); }}>
                Copy
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// ============================================================ Sub-components
const UserRow = ({ user: u, me, onEdit, onAction, onImpersonate }) => {
  const isOwner = me?.role === 'owner';
  const isSelf = me?.email === u.email;
  return (
    <Card data-testid={`ac-user-${u.id}`}>
      <CardContent className="py-3 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-[#0F172A]">{u.full_name || u.email}</span>
            <Badge className={`text-[9px] uppercase border ${ROLE_TONE[u.role]}`}>{u.role_label || u.role}</Badge>
            <Badge className={`text-[9px] ${STATUS_TONE[u.status]}`}>{u.status}</Badge>
            {isSelf && <Badge className="bg-blue-100 text-blue-700 text-[9px]">YOU</Badge>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {u.email} · {(u.modules_access || []).join(', ') || 'no modules'}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isOwner && !isSelf && u.status === 'active' && (
            <Button size="sm" variant="outline" onClick={() => onImpersonate(u)}
              data-testid={`ac-impersonate-${u.id}`}
              title="View as this user (Owner-only)"
              className="text-amber-700 border-amber-200">
              <UserCog className="w-3.5 h-3.5 mr-1" /> View as
            </Button>
          )}
          {!isSelf && (
            <Button size="sm" variant="outline" onClick={onEdit}
              data-testid={`ac-edit-${u.id}`}>
              <Edit3 className="w-3.5 h-3.5 mr-1" /> Manage
            </Button>
          )}
          {isOwner && !isSelf && u.status !== 'blocked' && (
            <Button size="sm" variant="outline"
              onClick={() => onAction(`/api/access-control/block/${u.id}`, { reason: 'Blocked from Access Control Center' })}
              data-testid={`ac-block-${u.id}`}
              className="text-rose-600 border-rose-200">
              <Ban className="w-3.5 h-3.5" />
            </Button>
          )}
          {isOwner && !isSelf && u.status === 'blocked' && (
            <Button size="sm" variant="outline"
              onClick={() => onAction(`/api/access-control/unblock/${u.id}`, { reason: 'Unblocked' })}
              data-testid={`ac-unblock-${u.id}`}
              className="text-emerald-600 border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5" />
            </Button>
          )}
          {isOwner && !isSelf && u.status !== 'soft_deleted' && (
            <Button size="sm" variant="outline"
              onClick={() => { if (window.confirm(`Soft-delete ${u.email}?`)) onAction(`/api/access-control/users/${u.id}`, {}, 'delete'); }}
              data-testid={`ac-delete-${u.id}`}
              className="text-rose-700 border-rose-300">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const InviteDialog = ({ open, onClose, roles, me, onCreated }) => {
  const { toast } = useToast();
  const [data, setData] = useState({ email: '', full_name: '', role: 'staff', modules_access: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!data.email.trim() || !data.full_name.trim()) {
      toast({ title: 'Email and name required', variant: 'destructive' }); return;
    }
    setBusy(true);
    try {
      const payload = {
        email: data.email.trim(),
        full_name: data.full_name.trim(),
        role: data.role,
        modules_access: data.modules_access.split(',').map(s => s.trim()).filter(Boolean),
      };
      const r = await axios.post(`${API_URL}/api/access-control/invite`, payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      onCreated(r.data);
      onClose();
      setData({ email: '', full_name: '', role: 'staff', modules_access: '' });
    } catch (e) {
      toast({ title: 'Invite failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };
  // Owners can assign any role; admin_l2 can't create owner/admin_l2
  const allowedRoles = roles.filter(r => {
    if (me?.role === 'owner') return true;
    return !['owner', 'admin_l2'].includes(r.key);
  });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="ac-invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite a new account</DialogTitle>
          <DialogDescription className="sr-only">Provision a user with a temporary password.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Email *" value={data.email}
            onChange={e => setData({ ...data, email: e.target.value })}
            data-testid="ac-invite-email" />
          <Input placeholder="Full name *" value={data.full_name}
            onChange={e => setData({ ...data, full_name: e.target.value })}
            data-testid="ac-invite-name" />
          <select className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"
            value={data.role} onChange={e => setData({ ...data, role: e.target.value })}
            data-testid="ac-invite-role">
            {allowedRoles.map(r => <option key={r.key} value={r.key}>L{r.level} · {r.label}</option>)}
          </select>
          <Input placeholder="Modules (comma-separated). Leave empty for default."
            value={data.modules_access}
            onChange={e => setData({ ...data, modules_access: e.target.value })}
            data-testid="ac-invite-modules" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0F172A] hover:bg-[#1E293B]"
            data-testid="ac-invite-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditUserDialog = ({ user, me, roles, onClose, onSaved }) => {
  const { toast } = useToast();
  const [role, setRole] = useState(user.role);
  const [modules, setModules] = useState((user.modules_access || []).join(', '));
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } };
  const allowedRoles = roles.filter(r => {
    if (me?.role === 'owner') return true;
    return !['owner', 'admin_l2'].includes(r.key);
  });
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/access-control/promote/${user.id}`, {
        new_role: role,
        modules_access: modules.split(',').map(s => s.trim()).filter(Boolean),
        reason: 'Updated via Access Control Center',
      }, auth);
      toast({ title: 'Updated' });
      onSaved();
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="ac-edit-dialog">
        <DialogHeader>
          <DialogTitle>Manage {user.full_name || user.email}</DialogTitle>
          <DialogDescription className="sr-only">Change role or module assignment.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Role</label>
            <select className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm mt-1"
              value={role} onChange={e => setRole(e.target.value)}
              data-testid="ac-edit-role">
              {allowedRoles.map(r => <option key={r.key} value={r.key}>L{r.level} · {r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Modules</label>
            <Input value={modules} onChange={e => setModules(e.target.value)}
              placeholder="Comma-separated. Use * for all (admin only)."
              data-testid="ac-edit-modules" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="bg-[#0F172A] hover:bg-[#1E293B]"
            data-testid="ac-edit-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AccessControlCenter;
